import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { setAuth } from '../utils/auth';
import { t } from '../utils/i18n';
import { useLanguage } from '../contexts/LanguageContext';

function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { currentLang, setLanguage, languages } = useLanguage();
    const [, forceUpdate] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            console.log('🔐 Attempting login for:', username);
            console.log('🔐 API URL:', process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
            const response = await authAPI.login(username, password);
            console.log('🔐 Login response:', response);
            console.log('🔐 Login response data:', response.data);
            
            if (response.data && response.data.success) {
                console.log('✅ Login successful!');
                console.log('Token:', response.data.token ? response.data.token.substring(0, 20) + '...' : 'null');
                console.log('User:', response.data.user);
                console.log('Permissions:', response.data.permissions);
                
                if (!response.data.token) {
                    console.error('❌ No token in response!');
                    setError('Login failed: No token received');
                    return;
                }
                
                setAuth(
                    response.data.token,
                    response.data.user,
                    response.data.permissions
                );
                
                // Verify token was saved
                const savedToken = localStorage.getItem('token');
                console.log('✅ Token saved to localStorage:', !!savedToken);
                console.log('Saved token value:', savedToken ? savedToken.substring(0, 20) + '...' : 'null');
                
                navigate('/dashboard');
            } else {
                console.error('❌ Login failed - no success flag');
                console.error('Response:', response);
                setError(response.data?.error || 'Login failed. Please try again.');
            }
        } catch (err) {
            console.error('❌ Login error:', err);
            console.error('Error name:', err.name);
            console.error('Error message:', err.message);
            console.error('Error code:', err.code);
            console.error('Error response:', err.response);
            console.error('Error response data:', err.response?.data);
            console.error('Error response status:', err.response?.status);
            console.error('Error config:', err.config);
            console.error('Full error object:', JSON.stringify(err, Object.getOwnPropertyNames(err)));
            
            // More specific error handling
            let errorMessage = 'Login failed. Please try again.';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                // Check if it's a network error
                if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
                    errorMessage = 'Network Error: Cannot connect to server. Please check if the server is running on http://localhost:5000';
                } else {
                    errorMessage = err.message;
                }
            }
            
            console.error('Setting error message:', errorMessage);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                {/* Logo above login */}
                <div className="login-logo-container">
                    <img 
                        src="/atom.png" 
                        alt="Անտրոհուս կրթական հիմնադրամ" 
                        className="login-logo"
                    />
                    <h2 className="login-platform-name">{t('platformName')}</h2>
                </div>

                {/* Language Selector in Login Card */}
                <div style={{ 
                    marginBottom: '24px'
                }}>
                    <select
                        className="form-select"
                        value={currentLang}
                        onChange={(e) => setLanguage(e.target.value)}
                        style={{
                            width: '100%'
                        }}
                    >
                        {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                                {lang.native}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="login-header">
                    <h1 className="login-title">{t('welcome')}</h1>
                    <p className="login-description">
                        {t('signInDescription')}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-error">
                            <i className="fas fa-exclamation-circle"></i>
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">{t('username')}</label>
                        <input
                            type="text"
                            className="form-input"
                            placeholder={t('username')}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">{t('password')}</label>
                        <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="form-input"
                                placeholder={t('password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                style={{ paddingRight: '50px', width: '100%', flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowPassword(!showPassword);
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                style={{
                                    position: 'absolute',
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '10px',
                                    color: '#666',
                                    fontSize: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 100,
                                    outline: 'none',
                                    minWidth: '30px',
                                    minHeight: '30px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#333';
                                    e.currentTarget.style.background = '#f0f0f0';
                                    e.currentTarget.style.borderRadius = '4px';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = '#666';
                                    e.currentTarget.style.background = 'transparent';
                                }}
                                title={showPassword ? 'Թաքցնել գաղտնաբառը' : 'Ցուցադրել գաղտնաբառը'}
                            >
                                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} style={{ pointerEvents: 'none' }}></i>
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-block"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <div className="loading"></div>
                                {t('loading')}
                            </>
                        ) : (
                            <>
                                <i className="fas fa-sign-in-alt"></i>
                                {t('signIn')}
                            </>
                        )}
                    </button>
                </form>

            </div>
        </div>
    );
}

export default Login;

